<template>
  <div class="relative">
    <nav class="fixed top-0 left-0 w-full px-4 py-5 backdrop-blur-md transition-all duration-300 ease-in-out z-50 border-b border-white/5">
      <div class="container mx-auto max-w-[1400px] px-6 lg:px-10 xl:px-8 flex justify-between items-center relative">
        <div v-motion-pop-visible class="flex items-center justify-start">
          <a href="/#" class="flex items-center justify-start" aria-label="Go to home page">
            <img src="@/assets/zach_dev_works_logo.avif" class="w-auto h-[32px] md:h-[48px] object-contain" alt="Zach Dev Works logo" width="150" height="48" />
          </a>
        </div>

        <div class="hidden lg:flex flex-1 items-center justify-between px-6">
          <div class="flex gap-4 items-center justify-start ml-8">
            <div v-for="item in groupedNavItems" :key="item.id" class="relative group">
              <!-- Direct Link -->
              <router-link
                v-if="!item.children"
                :to="item.hash"
                @click="handleNavClick"
                :class="['inline-flex items-center text-[15px] font-lato font-bold px-3 py-2 rounded transition-all duration-300 ease-in-out uppercase tracking-[0.15em] border-b-2 border-transparent', 
                  activeSection === item.id ? 'text-[#2AD1EE] border-[#2AD1EE]' : 'text-white/90 hover:text-white group-hover:border-[#2AD1EE]/30']"
              >
                {{ item.name }}
              </router-link>

              <!-- Dropdown Link -->
              <template v-else>
                <button
                  class="inline-flex items-center gap-2 text-[15px] font-lato font-bold px-3 py-2 rounded transition-all duration-300 ease-in-out uppercase tracking-[0.15em] border-b-2 border-transparent"
                  :class="[isChildActive(item) ? 'text-[#2AD1EE] border-[#2AD1EE]' : 'text-white/90 hover:text-white group-hover:border-[#2AD1EE]/30']"
                >
                  {{ item.name }}
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 transition-transform group-hover:rotate-180" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </button>
                
                <!-- Dropdown content -->
                <div class="absolute top-full left-0 mt-2 bg-[#0A1A38]/95 backdrop-blur-xl border border-white/10 rounded-xl py-3 min-w-[220px] shadow-2xl opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 z-50">
                  <router-link
                    v-for="child in item.children"
                    :key="child.id"
                    :to="child.hash"
                    @click="handleNavClick"
                    class="block px-6 py-3 text-sm font-bold uppercase tracking-widest transition-colors"
                    :class="[activeSection === child.id ? 'text-[#2AD1EE] bg-white/5' : 'text-white/70 hover:text-white hover:bg-white/5']"
                  >
                    {{ child.name }}
                  </router-link>
                </div>
              </template>
            </div>
          </div>
          <a href="https://discord.gg/smky9MMEGH" target="_blank" rel="noopener noreferrer" class="relative inline-flex items-center justify-center px-10 py-3 group" aria-label="Join our Discord community">
            <div class="absolute inset-0 bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE] transform -skew-x-[20deg]"></div>
            <span class="relative z-10 font-bro-mega font-black text-white text-sm uppercase">GET IN TOUCH!</span>
          </a>
        </div>

        <div class="lg:hidden">
          <button @click="toggleSidebar" class="relative z-[110] p-2 flex flex-col gap-1.5 w-8 items-end" aria-label="Toggle mobile menu">
            <span class="w-8 h-[2px] bg-white transition-all"></span>
            <span class="w-5 h-[2px] bg-[#2AD1EE]"></span>
            <span class="w-8 h-[2px] bg-white transition-all"></span>
          </button>
        </div>
      </div>
    </nav>

    <div 
      class="fixed inset-0 z-[100] flex justify-start transition-none"
      :class="isSidebarOpen ? 'pointer-events-auto' : 'pointer-events-none'"
    >
      <div 
        v-show="isSidebarOpen"
        @click="toggleSidebar" 
        class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-none"
      ></div>

      <transition name="slide-sidebar">
        <div
          v-if="isSidebarOpen"
          class="relative w-[300px] h-[100dvh] bg-[#061328] border-r border-white/10 shadow-2xl flex flex-col pointer-events-auto"
        >
          <div class="p-8 flex justify-between items-center border-b border-white/5">
            <span class="text-xs font-mono text-[#2AD1EE] tracking-widest uppercase opacity-60">SCROLL DOWN</span>
            <button @click="toggleSidebar" class="p-2 hover:bg-white/5 rounded-full" aria-label="Close menu">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <nav class="flex-1 flex flex-col p-6 pt-10 gap-4 overflow-y-auto">
            <div v-for="(item, index) in groupedNavItems" :key="item.id" class="flex flex-col">
              <!-- Direct Link -->
              <a
                v-if="!item.children"
                :href="item.hash"
                @click="toggleSidebar"
                class="group flex items-center justify-between p-4 rounded-xl transition-all hover:bg-white/5"
                :class="activeSection === item.id ? 'bg-white/5' : ''"
              >
                <div class="flex flex-col">
                  <span class="text-[10px] font-mono text-[#2AD1EE] opacity-50 mb-1">0{{ index + 1 }}</span>
                  <span class="text-xl font-bold uppercase tracking-widest text-white group-hover:text-[#2AD1EE]">
                    {{ item.name }}
                  </span>
                </div>
              </a>

              <!-- Dropdown / Accordion -->
              <template v-else>
                <div 
                  @click="toggleMobileDropdown(item.id)"
                  class="group flex items-center justify-between p-4 rounded-xl transition-all hover:bg-white/5 cursor-pointer"
                  :class="isChildActive(item) ? 'bg-white/5' : ''"
                >
                  <div class="flex flex-col">
                    <span class="text-[10px] font-mono text-[#2AD1EE] opacity-50 mb-1">0{{ index + 1 }}</span>
                    <span class="text-xl font-bold uppercase tracking-widest text-white group-hover:text-[#2AD1EE]">
                      {{ item.name }}
                    </span>
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-white transition-transform" :class="mobileDropdowns[item.id] ? 'rotate-180' : ''" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                
                <div v-show="mobileDropdowns[item.id]" class="flex flex-col pl-8 mt-2 gap-2">
                  <router-link
                    v-for="child in item.children"
                    :key="child.id"
                    :to="child.hash"
                    @click="toggleSidebar"
                    class="p-3 text-lg font-bold uppercase tracking-widest transition-colors"
                    :class="[activeSection === child.id ? 'text-[#2AD1EE]' : 'text-white/60 hover:text-white']"
                  >
                    {{ child.name }}
                  </router-link>
                </div>
              </template>
            </div>
          </nav>

          <div class="p-8 pb-12 border-t border-white/5">
            <a href="https://discord.gg/smky9MMEGH" target="_blank" rel="noopener noreferrer" class="flex items-center justify-center w-full py-4 bg-gradient-to-r from-[#2E71BF] to-[#2AD1EE] text-white font-bold text-xs uppercase tracking-widest rounded-lg" aria-label="Join our Discord community">
              Join Our Discord
            </a>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: "NavbarElement",
  props: {
    activeSection: { type: String, required: true },
  },
  data() {
    return {
      isSidebarOpen: false,
      mobileDropdowns: {
        services: false,
        portfolio: false
      },
      groupedNavItems: [
        { id: "home", name: "Home", hash: "/#home" },
        { 
          id: "services-group", 
          name: "Services", 
          children: [
            { id: "services", name: "Overview", hash: "/#services" },
            { id: "features", name: "Features", hash: "/#features" },
            { id: "house-games", name: "House Games", hash: "/#house-games" },
          ]
        },
        { 
          id: "portfolio-group", 
          name: "Portfolio", 
          children: [
            { id: "projects", name: "Projects", hash: "/projects" },
            { id: "partners", name: "Partners", hash: "/#partners" },
          ]
        },
        { id: "pricing", name: "Pricing", hash: "/#pricing" },
      ],
    };
  },
  methods: {
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
      document.body.style.overflow = this.isSidebarOpen ? 'hidden' : '';
    },
    handleNavClick() {
      if (this.isSidebarOpen) this.toggleSidebar();
    },
    toggleMobileDropdown(id) {
      this.mobileDropdowns[id] = !this.mobileDropdowns[id];
    },
    isChildActive(item) {
      if (!item.children) return this.activeSection === item.id;
      return item.children.some(child => child.id === this.activeSection);
    },
  }
};
</script>

<style scoped>
.slide-sidebar-enter-active {
  transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-sidebar-leave-active {
  transition: transform 0.4s cubic-bezier(0.7, 0, 0.84, 0);
}

.slide-sidebar-enter-from,
.slide-sidebar-leave-to {
  transform: translateX(-100%);
}

.slide-sidebar-enter-to,
.slide-sidebar-leave-from {
  transform: translateX(0);
}
</style>